import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Fade,
  Slide,
  FormHelperText,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import {
  Send as SendIcon,
  Verified as VerifiedIcon,
  School as SchoolIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { registerLaboratory, sendOTP, verifyOTP, checkExistingUser, getCountries, getStates, getCities } from '../services/api';

const steps = ['Basic Details', 'Contact Information', 'OTP Verification', 'Complete Registration'];

const RegistrationPage_temp = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);
  
  // Existing user authentication states
  const [existingUserAuthenticating, setExistingUserAuthenticating] = useState(false);
  const [existingUserAuthenticated, setExistingUserAuthenticated] = useState(false);
  const [externalApiData, setExternalApiData] = useState(null);
  
  // Dropdown data
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  
  // Form data state
  const [formData, setFormData] = useState({
    isExistingUser: '', // 'yes' or 'no'
    existingUsername: '',
    existingPassword: '',
    laboratoryName: '',
    labType: '',
    addressLine1: '',
    addressLine2: '',
    country: '101', // Default to India
    state: '',
    city: '',
    pincode: '',
    salutation: '',
    LabHeadName: '',
    LabHeadEmail: '',
    LabHeadMobile: '',
    ContactPersonEmail2: '',
  });

  // Form errors state
  const [errors, setErrors] = useState({});

  // OTP form data
  const [otpData, setOtpData] = useState({
    otp: '',
  });

  // OTP errors
  const [otpErrors, setOtpErrors] = useState({});

  // Handle form input changes
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle OTP input change
  const handleOtpChange = (event) => {
    const value = event.target.value;
    setOtpData(prev => ({
      ...prev,
      otp: value
    }));
    
    if (otpErrors.otp) {
      setOtpErrors(prev => ({
        ...prev,
        otp: ''
      }));
    }
  };

  // Validation functions
  const validateStep1 = () => {
    const newErrors = {};
    
    // Existing user validation
    if (!formData.isExistingUser) {
      newErrors.isExistingUser = 'Please specify if you are an existing user';
    }
    
    // If existing user is 'yes', validate username and password
    if (formData.isExistingUser === 'yes') {
      if (!formData.existingUsername.trim()) {
        newErrors.existingUsername = 'Username is required for existing users';
      }
      
      if (!formData.existingPassword.trim()) {
        newErrors.existingPassword = 'Password is required for existing users';
      }
      
      // Require authentication for existing users
      if (!existingUserAuthenticated) {
        newErrors.existingUserAuth = 'Please authenticate with your credentials';
      }
    }
    
    if (!formData.laboratoryName.trim()) {
      newErrors.laboratoryName = 'Laboratory name is required';
    }
    
    if (!formData.labType) {
      newErrors.labType = 'Laboratory type is required';
    }
    
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address line 1 is required';
    }
    
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    
    if (!formData.city) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.salutation) {
      newErrors.salutation = 'Salutation is required';
    }
    
    if (!formData.LabHeadName.trim()) {
      newErrors.LabHeadName = 'Head of Laboratory name is required';
    }
    
    if (!formData.LabHeadEmail.trim()) {
      newErrors.LabHeadEmail = 'Head of Laboratory email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.LabHeadEmail)) {
      newErrors.LabHeadEmail = 'Invalid email format';
    }
    
    if (!formData.LabHeadMobile.trim()) {
      newErrors.LabHeadMobile = 'Head of Laboratory mobile is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.LabHeadMobile)) {
      newErrors.LabHeadMobile = 'Invalid mobile number format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOtp = () => {
    const newErrors = {};
    
    if (!otpData.otp.trim()) {
      newErrors.otp = 'OTP is required';
    } else if (otpData.otp.length !== 6) {
      newErrors.otp = 'OTP must be 6 digits';
    }
    
    setOtpErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const labTypes = [
    { value: '1', label: 'Testing Laboratory', icon: '⚗️' },
     { value: '2', label: 'Calibration Laboratory', icon: '🔧' },
    { value: '3', label: 'Medical Laboratory', icon: '🏥' },
    // { value: '4', label: 'Proficiency Testing Provider', icon: '📊' },
    // { value: '5', label: 'Reference Material Producer', icon: '📊' },
  ];

  const salutations = [
    { value: 'Mr.', label: 'Mr.' },
    { value: 'Mrs.', label: 'Mrs.' },
    { value: 'Ms.', label: 'Ms.' },
    { value: 'Dr.', label: 'Dr.' },
    { value: 'Prof.', label: 'Prof.' },
  ];

  // Load initial data
  useEffect(() => {
    // Load states for India by default
    loadStates('101');
  }, []);

  // Load cities when state changes
  useEffect(() => {
    if (formData.state) {
      loadCities(formData.state);
      setFormData(prev => ({
        ...prev,
        city: ''
      }));
    }
  }, [formData.state]);

  // Resend OTP timer
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prevTimer => {
          if (prevTimer <= 1) {
            setCanResendOtp(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  const loadStates = async (countryId) => {
    try {
      const response = await getStates(countryId);
      const statesData = response.data?.data || response.data || [];
      setStates(Array.isArray(statesData) ? statesData : []);
    } catch (error) {
      console.error('Error loading states:', error);
      setStates([]);
    }
  };

  const loadCities = async (stateId) => {
    try {
      const response = await getCities(stateId);
      const citiesData = response.data?.data || response.data || [];
      setCities(Array.isArray(citiesData) ? citiesData : []);
    } catch (error) {
      console.error('Error loading cities:', error);
      setCities([]);
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    
    try {
      if (activeStep === 2 && !otpVerified) {
        handleSendOTP();
        return;
      }

      if (activeStep === 3) {
        await handleRegistration();
        return;
      }

      // For steps 0 and 1, use handleNext
      await handleNext();
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An error occurred. Please try again.');
    }
  };

  const handleSendOTP = async () => {
    if (!formData.LabHeadEmail || !formData.LabHeadMobile) {
      toast.error('Head of Laboratory email and mobile are required');
      return;
    }

    setLoading(true);
    try {
      await sendOTP({
        email: formData.LabHeadEmail,
        mobile: formData.LabHeadMobile,
      });
      setOtpDialogOpen(true);
      setResendTimer(60); // 1 minute countdown
      setCanResendOtp(false);
      toast.success('OTP sent successfully to your email and mobile');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (event) => {
    event.preventDefault();
    
    if (!validateOtp()) {
      return;
    }

    setLoading(true);
    try {
      await verifyOTP({
        email: formData.LabHeadEmail,
        mobile: formData.LabHeadMobile,
        otp: otpData.otp,
      });
      setOtpVerified(true);
      setOtpDialogOpen(false);
      setActiveStep(3);
      toast.success('OTP verified successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResendOtp || loading) {
      return;
    }

    setLoading(true);
    try {
      await sendOTP({
        email: formData.LabHeadEmail,
        mobile: formData.LabHeadMobile,
      });
      setResendTimer(60); // Reset 1 minute countdown
      setCanResendOtp(false);
      setOtpData({ otp: '' }); // Clear previous OTP
      toast.success('OTP resent successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // Authenticate existing user with external API
  const handleAuthenticateExistingUser = async () => {
    if (!formData.existingUsername.trim() || !formData.existingPassword.trim()) {
      toast.error('Please enter both username and password');
      return;
    }

    setExistingUserAuthenticating(true);
    try {
      // Call backend API which will call the external API
      const response = await checkExistingUser({
        username: formData.existingUsername,
        password: formData.existingPassword,
      });

      // Check response from backend
      if (!response.data.success) {
        throw new Error(response.data.message || 'Invalid UserName or Password');
      }

      const data = response.data.data;
      
      // Store the API response data for later use
      setExternalApiData(data);
      setExistingUserAuthenticated(true);
      toast.success('Authentication successful!');
    } catch (error) {
      console.error('Authentication error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Authentication failed. Please check your credentials.';
      toast.error(errorMessage);
    } finally {
      setExistingUserAuthenticating(false);
    }
  };

  const handleRegistration = async () => {
    setLoading(true);
    try {
      // Include external API data for existing users
      const registrationData = {
        ...formData,
        ...(externalApiData && {
          externalApiData: externalApiData
        })
      };
      
      await registerLaboratory(registrationData);
      toast.success('Laboratory registered successfully! Login credentials sent to your email.');
      setTimeout(() => {
        navigate('/main/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    try {
      // Validate current step before proceeding
      if (activeStep === 0) {
        if (!validateStep1()) {
          toast.error('Please fill all required fields correctly');
          return;
        }
      } else if (activeStep === 1) {
        if (!validateStep2()) {
          toast.error('Please fill all required fields correctly');
          return;
        }
      }
      
      setActiveStep(prevStep => prevStep + 1);
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('An error occurred during validation');
    }
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const getStepIcon = (step) => {
    switch (step) {
      case 0:
        return <BusinessIcon />;
      case 1:
        return <PersonIcon />;
      case 2:
        return <SecurityIcon />;
      case 3:
        return <CheckCircleIcon />;
      default:
        return <BusinessIcon />;
    }
  };

  const renderBasicDetails = () => (
    <Fade in={true} timeout={500}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <BusinessIcon sx={{ fontSize: 28, color: '#1e3c72', mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Laboratory Information
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
        </Grid>
        
        {/* Existing User Question */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 3, 
            border: '2px solid', 
            borderColor: errors.isExistingUser ? 'error.main' : '#e0e0e0',
            borderRadius: 2,
            backgroundColor: '#f8f9fa',
            mb: 2
          }}>
            <FormControl component="fieldset" error={!!errors.isExistingUser}>
              <FormLabel 
                component="legend" 
                sx={{ 
                  fontWeight: 600, 
                  fontSize: '1.1rem',
                  color: 'text.primary',
                  mb: 2,
                  '&.Mui-focused': {
                    color: 'text.primary',
                  }
                }}
              >
                Do you have accreditation login on the NABL old portal? *
              </FormLabel>
              <RadioGroup
                row
                value={formData.isExistingUser}
                onChange={handleInputChange('isExistingUser')}
                sx={{ mb: 2 }}
              >
                <FormControlLabel 
                  value="yes" 
                  control={<Radio sx={{ color: '#1e3c72' }} />} 
                  label="Yes" 
                  sx={{ mr: 4 }}
                />
                <FormControlLabel 
                  value="no" 
                  control={<Radio sx={{ color: '#1e3c72' }} />} 
                  label="No" 
                />
              </RadioGroup>
              {errors.isExistingUser && (
                <FormHelperText sx={{ fontSize: '0.875rem' }}>
                  {errors.isExistingUser}
                </FormHelperText>
              )}
            </FormControl>
            
            {/* Existing User Fields */}
            {formData.isExistingUser === 'yes' && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={formData.existingUsername}
                    onChange={handleInputChange('existingUsername')}
                    error={!!errors.existingUsername}
                    helperText={errors.existingUsername}
                    required
                    disabled={existingUserAuthenticated}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: '#1e3c72' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 20px rgba(30, 60, 114, 0.3)',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Password"
                    value={formData.existingPassword}
                    onChange={handleInputChange('existingPassword')}
                    error={!!errors.existingPassword}
                    helperText={errors.existingPassword}
                    required
                    disabled={existingUserAuthenticated}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SecurityIcon sx={{ color: '#1e3c72' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 20px rgba(30, 60, 114, 0.3)',
                        },
                      },
                    }}
                  />
                </Grid>
                
                {/* Authenticate Button */}
                {!existingUserAuthenticated && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleAuthenticateExistingUser}
                      disabled={existingUserAuthenticating || !formData.existingUsername.trim() || !formData.existingPassword.trim()}
                      startIcon={existingUserAuthenticating ? <CircularProgress size={20} /> : <SecurityIcon />}
                      fullWidth
                      sx={{
                        py: 1.5,
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1a2b5c 0%, #213d7a 50%, #2563eb 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(30, 60, 114, 0.3)',
                        },
                        '&:disabled': {
                          background: '#e0e0e0',
                          color: '#9e9e9e',
                        },
                      }}
                    >
                      {existingUserAuthenticating ? 'Authenticating...' : 'Authenticate User'}
                    </Button>
                  </Grid>
                )}
                
                {/* Authentication Success */}
                {existingUserAuthenticated && (
                  <Grid item xs={12}>
                    <Alert 
                      severity="success" 
                      sx={{ 
                        '& .MuiAlert-message': { 
                          display: 'flex', 
                          alignItems: 'center' 
                        } 
                      }}
                    >
                      <CheckCircleIcon sx={{ mr: 1 }} />
                      Authentication successful! You can now proceed with registration.
                      {externalApiData && externalApiData.Labid && (
                        <Typography variant="body2" sx={{ ml: 2, fontWeight: 'bold' }}>
                          Lab ID: {externalApiData.Labid}
                        </Typography>
                      )}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Laboratory Name"
            value={formData.laboratoryName}
            onChange={handleInputChange('laboratoryName')}
            error={!!errors.laboratoryName}
            helperText={errors.laboratoryName}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SchoolIcon sx={{ color: '#1e3c72' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
                '&.Mui-focused': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 20px rgba(30, 60, 114, 0.3)',
                },
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.labType}>
            <InputLabel>Laboratory Type *</InputLabel>
            <Select 
              value={formData.labType}
              onChange={handleInputChange('labType')}
              label="Laboratory Type *"
              sx={{
                backgroundColor: 'background.paper',
              }}
            >
              {labTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ mr: 1 }}>{type.icon}</Typography>
                    {type.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {errors.labType && (
              <FormHelperText>{errors.labType}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Address Line 1"
            value={formData.addressLine1}
            onChange={handleInputChange('addressLine1')}
            error={!!errors.addressLine1}
            helperText={errors.addressLine1}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon sx={{ color: '#1e3c72' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
                '&.Mui-focused': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 20px rgba(30, 60, 114, 0.3)',
                },
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Address Line 2 (Optional)"
            value={formData.addressLine2}
            onChange={handleInputChange('addressLine2')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.state}>
            <InputLabel>State *</InputLabel>
            <Select 
              value={formData.state}
              onChange={handleInputChange('state')}
              label="State *"
              sx={{
                backgroundColor: 'background.paper',
              }}
            >
              {Array.isArray(states) && states.map((state) => (
                <MenuItem key={state.id} value={state.id.toString()}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
            {errors.state && (
              <FormHelperText>{errors.state}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.city}>
            <InputLabel>City *</InputLabel>
            <Select 
              value={formData.city}
              onChange={handleInputChange('city')}
              label="City *" 
              disabled={!formData.state}
              sx={{
                backgroundColor: 'background.paper',
              }}
            >
              {Array.isArray(cities) && cities.map((city) => (
                <MenuItem key={city.id} value={city.id.toString()}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
            {errors.city && (
              <FormHelperText>{errors.city}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Pincode"
            value={formData.pincode}
            onChange={handleInputChange('pincode')}
            error={!!errors.pincode}
            helperText={errors.pincode}
            required
            inputProps={{ maxLength: 6 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
              },
            }}
          />
        </Grid>

        {formData.labType && (
          <Grid item xs={12}>
            <Chip
              label={`Selected: ${labTypes.find(t => t.value === formData.labType)?.label || ''}`}
              color="primary"
              variant="outlined"
              sx={{ 
                height: 32, 
                fontSize: '0.875rem',
                '& .MuiChip-label': { px: 2 }
              }}
            />
          </Grid>
        )}
      </Grid>
    </Fade>
  );

  const renderContactInformation = () => (
    <Fade in={true} timeout={500}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <PersonIcon sx={{ fontSize: 28, color: '#1e3c72', mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Head of Organization Details
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth error={!!errors.salutation}>
            <InputLabel>Salutation *</InputLabel>
            <Select 
              value={formData.salutation}
              onChange={handleInputChange('salutation')}
              label="Salutation *"
              sx={{
                backgroundColor: 'background.paper',
              }}
            >
              {salutations.map((sal) => (
                <MenuItem key={sal.value} value={sal.value}>
                  {sal.label}
                </MenuItem>
              ))}
            </Select>
            {errors.salutation && (
              <FormHelperText>{errors.salutation}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={9}>
          <TextField
            fullWidth
            label="Head of Laboratory Name"
            value={formData.LabHeadName}
            onChange={handleInputChange('LabHeadName')}
            error={!!errors.LabHeadName}
            helperText={errors.LabHeadName}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Head of Laboratory Email"
            type="email"
            value={formData.LabHeadEmail}
            onChange={handleInputChange('LabHeadEmail')}
            error={!!errors.LabHeadEmail}
            helperText={errors.LabHeadEmail}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: '#1e3c72' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
                '&.Mui-focused': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 20px rgba(30, 60, 114, 0.3)',
                },
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Head of Laboratory Mobile"
            value={formData.LabHeadMobile}
            onChange={handleInputChange('LabHeadMobile')}
            error={!!errors.LabHeadMobile}
            helperText={errors.LabHeadMobile}
            required
            inputProps={{ maxLength: 10 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: '#1e3c72' }} />
                  <Typography sx={{ ml: 0.5, mr: 1 }}>+91</Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
                '&.Mui-focused': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 20px rgba(30, 60, 114, 0.3)',
                },
              },
            }}
          />
        </Grid>

      </Grid>
    </Fade>
  );

  const renderOTPVerification = () => (
    <Fade in={true} timeout={500}>
      <Box textAlign="center">
        <SecurityIcon sx={{ fontSize: 64, color: '#1e3c72', mb: 2 }} />
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Verify Your Contact Details
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 500, mx: 'auto' }}>
          We'll send a 6-digit OTP to your email and mobile number for verification.
        </Typography>
        
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mt: 3, 
            mx: 'auto', 
            maxWidth: 400,
            backgroundColor: 'background.default',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
            <EmailIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
            {formData.LabHeadEmail}
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            <PhoneIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
            +91 {formData.LabHeadMobile}
          </Typography>
          
          {otpVerified ? (
            <Alert 
              severity="success" 
              sx={{ 
                '& .MuiAlert-message': { 
                  display: 'flex', 
                  alignItems: 'center' 
                } 
              }}
            >
              <VerifiedIcon sx={{ mr: 1 }} />
              Contact details verified successfully!
            </Alert>
          ) : (
            <Button
              variant="contained"
              onClick={handleSendOTP}
              disabled={loading || !formData.LabHeadEmail || !formData.LabHeadMobile}
              startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              size="large"
              fullWidth
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                boxShadow: '0 4px 15px rgba(30, 60, 114, 0.4)',
                transition: 'all 0.3s ease',
                borderRadius: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #2a5298 0%, #3b82f6 50%, #1e3c72 100%)',
                  boxShadow: '0 8px 25px rgba(30, 60, 114, 0.6)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </Button>
          )}
        </Paper>
      </Box>
    </Fade>
  );

  const renderSummary = () => (
    <Fade in={true} timeout={500}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 28, color: '#10b981', mr: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Registration Summary
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            backgroundColor: 'background.default',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Laboratory Name:
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {formData.laboratoryName}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Laboratory Type:
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {labTypes.find(t => t.value === formData.labType)?.label || ''}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Head of Laboratory:
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {formData.salutation} {formData.LabHeadName}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Email:
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {formData.LabHeadEmail}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Mobile:
              </Typography>
              <Typography variant="body1" color="text.secondary">
                +91 {formData.LabHeadMobile}
              </Typography>
            </Grid>
            {formData.ContactPersonEmail2 && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Alternative Contact Email:
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {formData.ContactPersonEmail2}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Address:
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {formData.addressLine1}, {formData.addressLine2 ? formData.addressLine2 + ', ' : ''}
                {cities.find(c => c.id.toString() === formData.city)?.name}, 
                {states.find(s => s.id.toString() === formData.state)?.name} - {formData.pincode}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            By completing this registration, you agree to NABL's terms and conditions. 
            Login credentials will be sent to your registered email address.
          </Typography>
        </Alert>
      </Box>
    </Fade>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderBasicDetails();
      case 1:
        return renderContactInformation();
      case 2:
        return renderOTPVerification();
      case 3:
        return renderSummary();
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #667eea 100%)',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 4, md: 12 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(46, 213, 115, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(0, 123, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(108, 92, 231, 0.2) 0%, transparent 50%)
          `,
          animation: 'float 6s ease-in-out infinite',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in={true} timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>

            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                mb: 1,
              }}
            >
              Laboratory Registration
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                mb: 3,
                textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
              }}
            >
              Register your laboratory with NABL for accreditation
            </Typography>
          </Box>
        </Fade>

        <Slide direction="up" in={true} timeout={800}>
          <Paper
            elevation={24}
            sx={{ 
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 3s ease-in-out infinite',
              },
            }}
          >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box sx={{ mb: 4 }}>
              <Stepper 
                activeStep={activeStep} 
                alternativeLabel={!isMobile}
                orientation={isMobile ? 'vertical' : 'horizontal'}
                sx={{
                  '& .MuiStepIcon-root': {
                    color: theme.palette.grey[400],
                    '&.Mui-active': {
                      color: '#1e3c72',
                    },
                    '&.Mui-completed': {
                      color: '#10b981',
                    },
                  },
                }}
              >
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel 
                      icon={getStepIcon(index)}
                      sx={{
                        '& .MuiStepLabel-label': {
                          fontWeight: activeStep === index ? 600 : 400,
                          color: activeStep === index ? '#1e3c72' : 'text.secondary',
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              
              {/* Progress bar */}
              <Box sx={{ mt: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={(activeStep / (steps.length - 1)) * 100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            </Box>

            <form onSubmit={handleFormSubmit}>
              <Box sx={{ mb: 4, minHeight: 400 }}>
                {getStepContent(activeStep)}
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
               <div>
                 <Typography variant="body1" sx={{ color: 'text.primary', mb: 2 }}>
            Already have an account? <a href='/main/login' style={{color:'#1e3c72'}} >Login here</a>
          </Typography>
          </div>
          <div>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                  size="large"
                  sx={{ 
                    px: 3,
                    mr: 2,
                    borderColor: '#1e3c72',
                    color: '#1e3c72',
                    '&:hover': {
                      borderColor: '#2a5298',
                      backgroundColor: 'rgba(30, 60, 114, 0.04)',
                    },
                  }}
                >
                  Back
                </Button>
                
                {activeStep < 2 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={loading}
                    size="large"
                    sx={{ 
                      px: 4,
                      background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                      boxShadow: '0 4px 15px rgba(30, 60, 114, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2a5298 0%, #3b82f6 50%, #1e3c72 100%)',
                        boxShadow: '0 8px 25px rgba(30, 60, 114, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || (activeStep === 2 && !otpVerified)}
                    startIcon={loading && <CircularProgress size={20} />}
                    size="large"
                    sx={{ 
                      px: 4,
                      background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                      boxShadow: '0 4px 15px rgba(30, 60, 114, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2a5298 0%, #3b82f6 50%, #1e3c72 100%)',
                        boxShadow: '0 8px 25px rgba(30, 60, 114, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {loading 
                      ? 'Processing...' 
                      : activeStep === steps.length - 1 
                        ? 'Complete Registration' 
                        : 'Send OTP'
                    }
                  </Button>
                )}
                </div>
              </Box>
            </form>
          </CardContent>
        </Paper>
      </Slide>

      {/* OTP Dialog */}
      <Dialog 
        open={otpDialogOpen} 
        onClose={() => setOtpDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <SecurityIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Enter OTP
          </Typography>
        </DialogTitle>
        <form onSubmit={handleVerifyOTP}>
          <DialogContent sx={{ px: 3 }}>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'center' }}>
              Please enter the 6-digit OTP sent to:
            </Typography>
            
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2, 
                mb: 3, 
                backgroundColor: 'background.default',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                📧 {formData.LabHeadEmail}
              </Typography>
              <Typography variant="body2">
                📱 +91 {formData.LabHeadMobile}
              </Typography>
            </Paper>
            
            <TextField
              fullWidth
              label="Enter 6-digit OTP"
              value={otpData.otp}
              onChange={handleOtpChange}
              error={!!otpErrors.otp}
              helperText={otpErrors.otp}
              inputProps={{ 
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.5em' }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
            
            {/* Resend OTP Section */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              {resendTimer > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Resend OTP in {Math.floor(resendTimer / 60)}:{(resendTimer % 60).toString().padStart(2, '0')}
                </Typography>
              ) : (
                <Button
                  variant="text"
                  onClick={handleResendOTP}
                  disabled={!canResendOtp || loading}
                  sx={{
                    color: '#1e3c72',
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    '&:hover': {
                      backgroundColor: 'rgba(30, 60, 114, 0.04)',
                    },
                  }}
                >
                  Resend OTP
                </Button>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
            <Button onClick={() => setOtpDialogOpen(false)} size="large">
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
              size="large"
              sx={{
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                boxShadow: '0 4px 15px rgba(30, 60, 114, 0.4)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2a5298 0%, #3b82f6 50%, #1e3c72 100%)',
                  boxShadow: '0 8px 25px rgba(30, 60, 114, 0.6)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

   
      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </Container>
    </Box>
  );
};

export default RegistrationPage_temp;